import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MyFromTextarea, MyFormSelect } from "@/components/zthd/MyFormField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useApps } from "@/hooks/apps";
import { RESULT_OPTIONS } from "@/constants/report-result";
import type { ReportResult } from "@/constants/report-result";
import type { App, Prd, Version } from "@/types/domain";

// The one report-doc form dialog (replaces the near-identical prds/versions
// copies). Three sources for the target version:
// - `prdId`   → selector over the PRD's linked versions
// - `pid`     → selector over the app's versions
// - `version` → fixed target (no selector), shows the doc-naming example

const makeSchema = (needsVersion: boolean) =>
  z.object({
    reportDocUrl: z
      .string()
      .min(1, "Please enter a Feishu document URL")
      .regex(
        /^https:\/\/stickerstyle\.feishu\.cn\/docx\/[A-Za-z0-9]+$/,
        "The URL must be a valid Feishu document URL (https://stickerstyle.feishu.cn/docx/xxx)"
      )
      .refine((value) => !value.includes("?"), "The URL must not contain '?'")
      .refine(
        (value) => !value.includes("from_copylink"),
        "The URL must not contain 'from_copylink'"
      )
      .transform((str) => str.trim()),
    versionId: z
      .string()
      .optional()
      .refine((v) => !needsVersion || !!v, "Please select a linked Version"),
    result: z.enum(["positive", "negative", "neutral"], {
      error: "Please select a test result",
    }),
    summary: z.string().min(1, "Summary cannot be empty"),
  });

export type ReportDocFormData = {
  url: string;
  versionId?: string;
  pid?: string;
  result: ReportResult;
  summary: string;
};

type VersionItem = { code: string; name: string; short: string; pid?: string };

const ReportDocDialog = ({
  open,
  onOpenChange,
  onSubmit,
  prdId,
  pid,
  version,
  defaultVersionId,
  isSubmitting,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ReportDocFormData) => void | Promise<void>;
  /** Selector over this PRD's linked versions. */
  prdId?: string;
  /** Selector over this app's versions. */
  pid?: string;
  /** Fixed target version (no selector, shows the naming example). */
  version?: Version;
  defaultVersionId?: string;
  isSubmitting?: boolean;
  error?: string | null;
}) => {
  const Apps = useApps();
  const needsVersion = !version;

  // Fetch the PRD's linked versions.
  const { data: prd } = useSWR<Prd>(open && prdId ? [`/prds/${prdId}`] : null);
  // Or the app's versions.
  const { data: vdata } = useSWR<{ data: Version[]; total: number }>(
    open && !prdId && pid ? swrKey("/versions", { pid, pageSize: 200 }) : null
  );

  const versions: VersionItem[] = React.useMemo(() => {
    if (prdId) {
      // Scheduled engagements carry the version denormalized.
      return (prd?.engagements ?? [])
        .filter((e) => !!e.versionId)
        .map((e) => {
          const name = e.versionName || e.versionCode || String(e.versionId);
          return { code: String(e.versionId), name, short: name, pid: e.appId };
        });
    }
    return (vdata?.data ?? []).map((v) => {
      const name = v.docName || v.versionName || v.versionCode || v.id;
      return { code: v.id, name, short: name, pid: v.pid };
    });
  }, [prdId, prd, vdata]);

  const schema = React.useMemo(() => makeSchema(needsVersion), [needsVersion]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      reportDocUrl: "",
      versionId: defaultVersionId ?? "",
      result: undefined,
      summary: "",
    },
  });

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    try {
      const selectedVersion = version
        ? undefined
        : versions.find((v) => v.code === values.versionId);
      await onSubmit({
        url: values.reportDocUrl,
        versionId: version ? version.id : values.versionId,
        pid: version ? version.pid : selectedVersion?.pid,
        result: values.result,
        summary: values.summary,
      });
      form.reset();
    } catch {
      // Errors are handled by the parent component.
    }
  };

  React.useEffect(() => {
    if (open) {
      form.reset({
        reportDocUrl: "",
        versionId: defaultVersionId ?? "",
        result: undefined,
        summary: "",
      });
    }
  }, [open, defaultVersionId, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>New report</DialogTitle>
          <DialogDescription>
            {needsVersion
              ? "Enter a Feishu document URL and select a linked Version; the system will fetch the document info automatically."
              : "Enter a Feishu document URL; the system will fetch the document info automatically."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="flex flex-col space-y-2">
              <ul className="list-disc pl-4 text-sm text-destructive">
                <li>Accepts a Feishu document URL</li>
                <li>https://stickerstyle.feishu.cn/docx/xxx</li>
                <li>Must not contain &apos;from_copylink&apos;</li>
              </ul>
              {version && (
                <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  <p className="mb-1 font-medium">Document name example:</p>
                  <p className="text-xs">
                    {(() => {
                      const app = Apps.find(
                        (app: App) => app.code === version.pid
                      );
                      const appName = app?.name || version.pid;
                      return `${appName}-${version.versionName || version.versionCode}-30%`;
                    })()}
                  </p>
                  <p className="mt-1 text-xs">
                    Format: {`{App}-{Version}-{Publish Rate}`}
                  </p>
                </div>
              )}
              <MyFromTextarea
                name="reportDocUrl"
                form={form}
                label="Document URL"
                placeholder="https://stickerstyle.feishu.cn/docx/..."
                rows={3}
              />
              {needsVersion && (
                <MyFormSelect
                  name="versionId"
                  form={form}
                  items={versions}
                  label="Linked Version"
                  placeholder="Select a Version"
                  showShort={false}
                />
              )}
              <FormField
                control={form.control}
                name="result"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Test result</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-row space-x-4"
                      >
                        {RESULT_OPTIONS.map((r) => (
                          <div
                            key={r.value}
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem value={r.value} id={r.value} />
                            <Label htmlFor={r.value} className="cursor-pointer">
                              {r.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <MyFromTextarea
                name="summary"
                form={form}
                label="Summary"
                placeholder="Enter the key points of the document…"
                rows={4}
              />
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={isSubmitting || form.formState.isSubmitting}
              >
                {isSubmitting || form.formState.isSubmitting
                  ? "Submitting…"
                  : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDocDialog;
